import React from 'react';
import { TopCarrier, TransitTimeCarrier } from '../../../../types/dashboard.types';
import Card, { cardStyles } from '../../../components/ui/Card/Card';
import styles from './CarriersCard.module.css';

/**
 * QUIÉN MUEVE LO SUYO Y CUÁNTO TARDA (20-oct-2026).
 *
 * > *"falta algo de la columna de la derecha queda muy vacia"*
 *
 * Junta dos cosas que el API ya mandaba a todo el que tiene `LOADS` o `LD_VIEW` y nadie mostraba: `topCarriers`
 * (cuántas cargas lleva cada transportista) y `transitTimeByCarrier` (días promedio de salida a entrega).
 *
 * - **Arriba, el tránsito promedio de todas**, ponderado por cargas: un transportista con 2 cargas no pesa como uno
 *   con 100.
 * - **Hasta 5 transportistas**, con sus cargas, una barra relativa al que más lleva y su promedio de días.
 * - Las cargas sin transportista (`Unknown`) no se listan: al cliente no le dicen nada.
 */

interface CarriersCardProps {
  top?: TopCarrier[];
  transit?: TransitTimeCarrier[];
}

const SHOWN = 5;
const PALETTE = ['#f5a623', '#2f9fd0', '#22a04a', '#00a2ae', '#e8590c'];

const initials = (name: string): string =>
  name
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0]?.toUpperCase())
    .join('') || '?';

const days = (value: number): string => `${value.toFixed(1)} ${value === 1 ? 'day' : 'days'}`;

const CarriersCard: React.FC<CarriersCardProps> = ({ top = [], transit = [] }) => {
  const known = (c: { carrier_id: string | null; carrier_name: string }) =>
    Boolean(c.carrier_id) && c.carrier_name !== 'Unknown';

  const avgOf = new Map(
    transit.filter(known).map((t) => [t.carrier_id, Number(t.avg_transit_days)] as const)
  );

  const rows = top
    .filter((c) => known(c) && c.total_loads > 0)
    .slice(0, SHOWN)
    .map((c) => ({ ...c, avg: avgOf.get(c.carrier_id) }));
  const max = Math.max(1, ...rows.map((r) => r.total_loads));

  /** Ponderado por cargas, y solo con promedios que tienen sentido (> 0). */
  const timed = transit.filter((t) => known(t) && Number(t.avg_transit_days) > 0 && t.total_loads > 0);
  const timedLoads = timed.reduce((sum, t) => sum + t.total_loads, 0);
  const overall = timedLoads
    ? timed.reduce((sum, t) => sum + Number(t.avg_transit_days) * t.total_loads, 0) / timedLoads
    : null;

  return (
    <Card className={styles.card}>
      <div className={cardStyles.header}>
        <h3 className={cardStyles.title}>Your carriers</h3>
      </div>

      {overall !== null && (
        <div className={styles.summary}>
          <span className={styles.big}>{overall.toFixed(1)}</span>
          <span className={styles.bigLabel}>
            days average transit
            <span className={styles.hint}>from pickup to delivery</span>
          </span>
        </div>
      )}

      {rows.length === 0 ? (
        <p className={styles.empty}>No carriers assigned yet.</p>
      ) : (
        <ul className={styles.list}>
          {rows.map((c, i) => (
            <li
              key={c.carrier_id}
              className={styles.item}
              style={{ '--tone': PALETTE[i % PALETTE.length] } as React.CSSProperties}
            >
              <span className={styles.avatar} aria-hidden="true">
                {initials(c.carrier_name)}
              </span>
              <span className={styles.body}>
                <span className={styles.line}>
                  <span className={styles.name}>{c.carrier_name}</span>
                  <span className={styles.loads}>
                    {c.total_loads.toLocaleString()} {c.total_loads === 1 ? 'load' : 'loads'}
                  </span>
                </span>
                <span className={styles.track} aria-hidden="true">
                  <span className={styles.bar} style={{ width: `${(c.total_loads / max) * 100}%` }} />
                </span>
                {c.avg !== undefined && c.avg > 0 && (
                  <span className={styles.avg}>{days(c.avg)} average transit</span>
                )}
              </span>
            </li>
          ))}
        </ul>
      )}
    </Card>
  );
};

export default CarriersCard;
